<script setup lang="ts">
import { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import EditableElement from "@/views/page/EditableElement.vue";
import { computed } from "vue";
import type { CenteredProps } from "./CenteredProps";
import Feature from "@/views/blocks/shared/section/features/Feature.vue";
import Features from "@/views/blocks/shared/section/features/Features.vue";
import Section from "@/views/blocks/shared/section/layout/section/Section.vue";
import FullSpacing from "@/views/blocks/shared/section/layout/spacing/FullSpacing.vue";

// Import shared components
import Heading from "@/views/blocks/shared/section/typography/heading/Heading.vue";
import Paragraph from "@/views/blocks/shared/section/typography/paragraph/Paragraph.vue";
import Image from "@/views/blocks/shared/section/media/image/Image.vue";

const props = defineProps<{
    block: PageBlock<CenteredProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <Section :id="block.webId" :backgroundColor="styleValues.background">
        <FullSpacing>
            <div class="max-w-2xl lg:max-w-3xl mx-auto">
                <EditableElement
                    v-if="!dataValues.tag.isHidden"
                    :blockId="block.id"
                    fieldName="tag"
                >
                    <Heading
                        :text="dataValues.tag.data.text"
                        :tag="dataValues.tag.data.tag"
                        :textColor="styleValues.brand"
                        class="text-base/7 mb-2"
                    />
                </EditableElement>

                <EditableElement
                    v-if="!dataValues.title.isHidden"
                    :blockId="block.id"
                    fieldName="title"
                >
                    <Heading
                        :text="dataValues.title.data.text"
                        :tag="dataValues.title.data.tag"
                        :textColor="styleValues.text"
                        class="text-4xl lg:text-5xl mb-6"
                    />
                </EditableElement>

                <EditableElement
                    v-if="!dataValues.description.isHidden"
                    :blockId="block.id"
                    fieldName="description"
                >
                    <Paragraph
                        :text="dataValues.description.data.text"
                        :textColor="styleValues.text"
                        class="text-xl/8 opacity-70"
                    />
                </EditableElement>

                <div class="mt-10 max-w-2xl">
                    <EditableElement
                        v-if="!dataValues.topText.isHidden"
                        :blockId="block.id"
                        fieldName="topText"
                    >
                        <Paragraph
                            :text="dataValues.topText.data.text"
                            :textColor="styleValues.text"
                            class="text-base/7 opacity-70"
                        />
                    </EditableElement>

                    <Features class="mt-8">
                        <EditableElement
                            v-for="feature in dataValues.features.data.items"
                            :key="feature.id"
                            :blockId="block.id"
                            :fieldName="`features_${feature.id}`"
                            :hideable="false"
                        >
                            <Feature
                                :title="feature.title"
                                :description="feature.description"
                                :iconSrc="feature.iconSrc"
                                :titleTextColor="styleValues.text"
                                :descriptionTextColor="styleValues.text"
                            />
                        </EditableElement>
                    </Features>

                    <EditableElement
                        v-if="!dataValues.bottomText.isHidden"
                        :blockId="block.id"
                        fieldName="bottomText"
                    >
                        <Paragraph
                            :text="dataValues.bottomText.data.text"
                            :textColor="styleValues.text"
                            class="text-base/7 mt-8 opacity-70"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.secondaryTitle.isHidden"
                        :blockId="block.id"
                        fieldName="secondaryTitle"
                    >
                        <Heading
                            :text="dataValues.secondaryTitle.data.text"
                            :tag="dataValues.secondaryTitle.data.tag"
                            :textColor="styleValues.text"
                            class="text-3xl mt-16"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.secondaryDescriptionOne.isHidden"
                        :blockId="block.id"
                        fieldName="secondaryDescriptionOne"
                    >
                        <Paragraph
                            :text="dataValues.secondaryDescriptionOne.data.text"
                            :textColor="styleValues.text"
                            class="text-base/7 mt-6 opacity-70"
                        />
                    </EditableElement>

                    <!-- Testimonial section -->
                    <EditableElement
                        v-if="!dataValues.testimonial.isHidden"
                        :blockId="block.id"
                        fieldName="testimonial"
                    >
                        <div
                            class="mt-10 pl-9 border-l"
                            :style="{
                                borderColor: styleValues.brand,
                            }"
                        >
                            <Paragraph
                                :text="dataValues.testimonial.data.text"
                                :textColor="styleValues.text"
                                class="text-base/7 italic"
                            />
                            <div class="flex items-center gap-4 mt-6 text-sm/6">
                                <Image
                                    :src="dataValues.testimonial.data.personImageSrc"
                                    :alt="dataValues.testimonial.data.personName"
                                    class="w-6 h-6 rounded-full"
                                />
                                <div class="flex items-center gap-1">
                                    <Paragraph
                                        :text="dataValues.testimonial.data.personName"
                                        :textColor="styleValues.text"
                                        class="font-semibold"
                                    />
                                    <span
                                        v-if="
                                            dataValues.testimonial.data.personName &&
                                            dataValues.testimonial.data.personPosition
                                        "
                                        :style="{ color: styleValues.text }"
                                    >
                                        -
                                    </span>
                                    <Paragraph
                                        :text="dataValues.testimonial.data.personPosition"
                                        :textColor="styleValues.text"
                                        class="opacity-70"
                                    />
                                </div>
                            </div>
                        </div>
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.secondaryDescriptionTwo.isHidden"
                        :blockId="block.id"
                        fieldName="secondaryDescriptionTwo"
                    >
                        <Paragraph
                            :text="dataValues.secondaryDescriptionTwo.data.text"
                            :textColor="styleValues.text"
                            class="text-base/7 mt-10 opacity-70"
                        />
                    </EditableElement>
                </div>

                <div class="mt-16">
                    <EditableElement
                        v-if="!dataValues.image.isHidden"
                        :blockId="block.id"
                        fieldName="image"
                    >
                        <Image
                            :src="dataValues.image.data.src"
                            :alt="dataValues.image.data.alt"
                            class="w-full rounded-lg aspect-[16/9] object-cover"
                            width="768"
                            height="432"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.infoNote.isHidden"
                        :blockId="block.id"
                        fieldName="infoNote"
                    >
                        <div
                            class="flex items-center gap-2 mt-4"
                            :style="{
                                color: styleValues.text,
                            }"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                                class="w-5 h-5"
                            >
                                <path
                                    fill-rule="evenodd"
                                    d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z"
                                    clip-rule="evenodd"
                                />
                            </svg>
                            <Paragraph
                                :text="dataValues.infoNote.data.text"
                                :textColor="styleValues.text"
                                class="text-sm/6 opacity-70"
                            />
                        </div>
                    </EditableElement>
                </div>

                <!-- Tertiary content -->
                <div class="max-w-2xl mt-16">
                    <EditableElement
                        v-if="!dataValues.tertiaryTitle.isHidden"
                        :blockId="block.id"
                        fieldName="tertiaryTitle"
                    >
                        <Heading
                            :text="dataValues.tertiaryTitle.data.text"
                            :tag="dataValues.tertiaryTitle.data.tag"
                            :textColor="styleValues.text"
                            class="text-2xl mb-6"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.tertiaryDescriptionOne.isHidden"
                        :blockId="block.id"
                        fieldName="tertiaryDescriptionOne"
                    >
                        <Paragraph
                            :text="dataValues.tertiaryDescriptionOne.data.text"
                            :textColor="styleValues.text"
                            class="text-base/7 mb-8 opacity-70"
                        />
                    </EditableElement>

                    <EditableElement
                        v-if="!dataValues.tertiaryDescriptionTwo.isHidden"
                        :blockId="block.id"
                        fieldName="tertiaryDescriptionTwo"
                    >
                        <Paragraph
                            :text="dataValues.tertiaryDescriptionTwo.data.text"
                            :textColor="styleValues.text"
                            class="text-base/7 opacity-70"
                        />
                    </EditableElement>
                </div>
            </div>
        </FullSpacing>
    </Section>
</template>
